import { Box, Button, Sheet } from "@mui/joy";
import DarkTypography from "../../../common/DarkTypography";
import {
  PERMISSION_SECTIONS,
  PERMISSIONS_MAP,
  getApiPermissionLabel,
} from "../../../../api/zekay/permissions";
import useAppSettings from "../../../../contexts/app/appSettings";
import {
  colorTokens,
  permissionsPickerModuleGroupsSx,
  permissionsPickerPanelTitleSx,
  permissionsPickerSheetCompactSx,
  permissionsPickerSheetFillSx,
  rolePermissionChipsRowSx,
  rolePermissionModuleHeadingSx,
  withAlpha,
} from "../../../../styles/globalStyles";

type PermissionsPickerProps = {
  allPermissions: string[];
  selectedPerms: string[];
  onChange: (perms: string[]) => void;
  loading: boolean;
  disabled?: boolean;
  /** When true, the list grows to fill parent height (e.g. large modals). */
  fillHeight?: boolean;
};

export default function PermissionsPicker(props: PermissionsPickerProps) {
  const {
    allPermissions,
    selectedPerms,
    onChange,
    loading,
    disabled = false,
    fillHeight = false,
  } = props;
  const { config } = useAppSettings();
  const isDesync = config?.CustomersSync === false;
  const sectionsToShow = PERMISSION_SECTIONS.filter(
    (s) => s.showOnlyWhenDesync !== true || isDesync,
  );
  const desyncOnlyApiKeys = new Set<string>(
    PERMISSION_SECTIONS.filter((s) => s.showOnlyWhenDesync === true).flatMap((s) =>
      s.keys.map((k) => PERMISSIONS_MAP[k].apiKey),
    ),
  );
  const displayPermissions = isDesync
    ? allPermissions
    : allPermissions.filter((p) => !desyncOnlyApiKeys.has(p));

  const toggle = (perm: string) => {
    if (disabled) return;
    onChange(
      selectedPerms.includes(perm)
        ? selectedPerms.filter((p) => p !== perm)
        : [...selectedPerms, perm],
    );
  };

  const allSet = new Set(displayPermissions);
  const shownApiKeys = new Set<string>();
  sectionsToShow.forEach((section) => {
    section.keys.forEach((k) => {
      const apiKey = PERMISSIONS_MAP[k].apiKey;
      if (allSet.has(apiKey)) shownApiKeys.add(apiKey);
    });
  });
  const otherApiKeys = displayPermissions.filter((apiKey) => !shownApiKeys.has(apiKey));

  const renderPermissionButtons = (apiKeys: string[]) => (
    <Box sx={rolePermissionChipsRowSx}>
      {apiKeys.map((apiKey) => {
        const active = selectedPerms.includes(apiKey);
        return (
          <Button
            key={apiKey}
            size="sm"
            variant="plain"
            disabled={disabled}
            sx={{
              border: `1px solid ${colorTokens.border.default}`,
              color: active ? colorTokens.accent.contrastText : colorTokens.text.primary,
              backgroundColor: active ? colorTokens.accent.primary : "transparent",
              "&:hover": {
                backgroundColor: active
                  ? colorTokens.accent.hover
                  : withAlpha(colorTokens.text.primary, 0.08),
              },
            }}
            onClick={() => toggle(apiKey)}
          >
            {getApiPermissionLabel(apiKey)}
          </Button>
        );
      })}
    </Box>
  );

  const sheetSx = fillHeight ? permissionsPickerSheetFillSx : permissionsPickerSheetCompactSx;

  const inner = (
    <>
      <Box sx={{ mt: fillHeight ? 0 : 1 }}>
        <DarkTypography level="body-sm" sx={permissionsPickerPanelTitleSx}>
          Permissions
        </DarkTypography>
      </Box>
      <Sheet variant="soft" sx={sheetSx}>
        {loading ? (
          <DarkTypography level="body-sm" colorVariant="muted" sx={{ px: 1 }}>
            Chargement des permissions...
          </DarkTypography>
        ) : displayPermissions.length === 0 ? (
          <DarkTypography level="body-sm" colorVariant="muted" sx={{ px: 1 }}>
            Aucune permission déclarée.
          </DarkTypography>
        ) : (
          <Box sx={permissionsPickerModuleGroupsSx}>
            {sectionsToShow.map((section) => {
              const apiKeysInSection = section.keys
                .map((k) => PERMISSIONS_MAP[k].apiKey)
                .filter((apiKey) => allSet.has(apiKey));
              if (apiKeysInSection.length === 0) return null;
              return (
                <Box key={section.label}>
                  <DarkTypography
                    level="body-xs"
                    colorVariant="muted"
                    sx={rolePermissionModuleHeadingSx}
                  >
                    {section.label}
                  </DarkTypography>
                  {renderPermissionButtons(apiKeysInSection)}
                </Box>
              );
            })}
            {otherApiKeys.length > 0 && (
              <Box>
                <DarkTypography
                  level="body-xs"
                  colorVariant="muted"
                  sx={rolePermissionModuleHeadingSx}
                >
                  Autres
                </DarkTypography>
                {renderPermissionButtons(otherApiKeys)}
              </Box>
            )}
          </Box>
        )}
      </Sheet>
    </>
  );

  if (fillHeight) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, width: "100%" }}>
        {inner}
      </Box>
    );
  }

  return <>{inner}</>;
}
